import { useContext, useEffect, useState } from "react";
import {
  DestinationStep,
  STEP_CONFIRM_TRANSFER_DETAILS,
  STEP_DESTINATION_ACCOUNT,
  STEP_DESTINATION_CHOICES,
  STEP_DESTINATION_CONNECT_ES,
  STEP_DESTINATION_CONNECT_ES_DETAILS,
  STEP_DESTINATION_CONNECT_ES_UPLOAD_STATEMENT,
  STEP_DESTINATION_CONNECT_IRA,
  STEP_DESTINATION_FLASH_LOADING,
  STEP_DESTINATION_HELP,
  STEP_DESTINATION_HELP_RESULT,
  STEP_DESTINATION_OPEN_AND_CONNECT_IRA,
  STEP_DESTINATION_OPTIONS,
  STEP_DESTINATION_OPTIONS_TYPE,
} from "../../class/transferDetails";
import Analytics from "../../utils/productAnalytics";
import { TransferContext } from "../../context/TransferContext";
import DestinationAccount from "../../class/destinationAccount";
import { AppContext } from "../../context/AppContext";
import { BRIDGE_DEMO } from "../../typings";
import { type UploadedFile } from "@manifesthhqinc/ui-library";
import manualWait from "../../utils/manualWait";
import { FileUploadStatus, UPLOAD_SUCCESS } from "../../class/file";
import { PrimaryDestinationChoice, TransferIntent, TransferIntentDestination } from "../../class/transferIntent";

export type DestinationAccountControllerType = ReturnType<typeof useDestinationAccount>;

const useDestinationAccount = () => {
  const { appEnv } = useContext(AppContext);
  const {
    goToScreen,
    transferIntentDetails,
    isLoading,
    createDestainationInAPI,
    transferDetails,
    setIsLoading,
    getEmployerOptions,
    providerList,
    currentScreen,
    uploadDestiationFile,
    getUserTransferOptions,
    transferOptions,
    deleteCurrentDestination,
    deleteDestinationAccountStatement,
    setIsDestinationAnimationLoaded,
    isIRAFoundAnimationLoaded,
    setIsIRAFoundAnimationLoaded,
    setSourceOfDestination,
    sourceOfDestination,
  } = useContext(TransferContext);

  const [currentDestinationDetails, setCurrentDestinationDetails] = useState<DestinationAccount>(
    new DestinationAccount(),
  );
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [confirmedTradIRANumber, setConfirmedTradIRANumber] = useState<string>("");
  const [confirmedRothIRANumber, setConfirmedRothIRANumber] = useState<string>("");
  const [formValidation, setFormValidation] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [lockProvider, setLockProvider] = useState<boolean>(false);
  const [isEmployerSponsored, setIsEmployerSponsored] = useState<boolean>(false);
  const [userChoice, setUserChoice] = useState<"EXPLORE_MY_OPTIONS" | "KNOW_MY_OPTION">("EXPLORE_MY_OPTIONS");
  const [selectedClientDestination, setSelectedClientDestination] = useState<DestinationAccount | null>(null);
  const [destinationChoiceError, setDestinationChoiceError] = useState<string>("");

  const redirectToOptions = () => goToScreen(STEP_DESTINATION_OPTIONS);

  const validateFormDetails = (details: Partial<DestinationAccount>) => {
    let isValidated = true;
    let validation = {};
    if (currentScreen === STEP_DESTINATION_CONNECT_IRA) {
      const iraValidation = {
        providerName: !details.providerName ? "Please provide account name" : "",
        confirmedTradIRANumber:
          details.traditionalIraAccountNumber !== confirmedTradIRANumber
            ? "Please confirm your traditional IRA account number"
            : "",
        confirmedRothIRANumber:
          details.rothIraAccountNumber !== confirmedRothIRANumber ? "Please confirm your roth IRA account number" : "",
        accountNumber:
          !details.traditionalIraAccountNumber && !details.rothIraAccountNumber
            ? "Please provide either Traditional IRA or Roth IRA number"
            : "",
      };
      isValidated = Object.values(iraValidation).filter((e) => e).length === 0;
      validation = iraValidation;
    }
    if (currentScreen === STEP_DESTINATION_CONNECT_ES) {
      isValidated = details.planSponsor !== "";
      validation = {
        planSponsor: details.planSponsor === "" ? "Please provide your current employer name" : "",
      };
    }
    if (currentScreen === STEP_DESTINATION_CONNECT_ES_UPLOAD_STATEMENT) {
      isValidated = details?.destinationStatementFileNames?.length
        ? details?.destinationStatementFileNames?.length > 0
        : false;
      validation = {
        destinationStatementFileNames:
          details?.destinationStatementFileNames?.length && details?.destinationStatementFileNames?.length > 0
            ? ""
            : "Please upload a statement to continue",
      };
    }
    if (currentScreen === STEP_DESTINATION_CONNECT_ES_DETAILS) {
      validation = {
        providerName: !details.providerName ? "Please provide the account provider name" : "",
      };
      isValidated = Object.values(validation).filter((e) => e).length === 0;
    }

    return { isValidated, validation };
  };

  useEffect(() => {
    setFormValidation({});
  }, [currentDestinationDetails, confirmedRothIRANumber, confirmedTradIRANumber]);

  const onClickContinue = async () => {
    // Validate form details
    const { isValidated, validation } = validateFormDetails(currentDestinationDetails);
    if (!isValidated) {
      setFormValidation(validation);
      return;
    }
    if (currentScreen === STEP_DESTINATION_OPEN_AND_CONNECT_IRA) {
      setLockProvider(true);
      goToScreen(STEP_DESTINATION_CONNECT_IRA);
    }
    if (currentScreen === STEP_DESTINATION_FLASH_LOADING) {
      if (sourceOfDestination === "CLIENT_FIX") {
        goToScreen(STEP_DESTINATION_ACCOUNT);
      } else if (sourceOfDestination === "CLIENT_OPTIONS") {
        goToScreen(STEP_DESTINATION_CHOICES);
      } else {
        goToScreen(STEP_DESTINATION_OPTIONS);
      }
    }
    if (currentScreen === STEP_DESTINATION_ACCOUNT) {
      Analytics.track({ name: "Confirmed destination account" });
      if (transferDetails.destinationAccount === null) {
        if (!transferIntentDetails) return;
        await createDestainationInAPI(currentDestinationDetails);
      }

      goToScreen(STEP_CONFIRM_TRANSFER_DETAILS);
    }
    if (
      currentScreen === STEP_DESTINATION_CONNECT_IRA ||
      currentScreen === STEP_DESTINATION_CONNECT_ES_UPLOAD_STATEMENT ||
      currentScreen === STEP_DESTINATION_CONNECT_ES_DETAILS
    ) {
      await createDestainationInAPI(new DestinationAccount({ ...currentDestinationDetails, yetToOpen: false }));
      goToScreen(STEP_DESTINATION_ACCOUNT);
    }

    if (currentScreen === STEP_DESTINATION_CONNECT_ES) {
      goToScreen(STEP_DESTINATION_CONNECT_ES_UPLOAD_STATEMENT);
      return;
    }
  };

  useEffect(() => {
    if (currentScreen === STEP_DESTINATION_OPEN_AND_CONNECT_IRA) {
      setCurrentDestinationDetails(transferDetails.destinationAccount || new DestinationAccount());
    } else if (currentScreen === STEP_DESTINATION_ACCOUNT && transferDetails.destinationAccount) {
      setCurrentDestinationDetails(transferDetails.destinationAccount);
    } else if (currentScreen === STEP_DESTINATION_ACCOUNT) {
      // TODO: do nothgin here
    } else if (currentScreen === STEP_DESTINATION_CONNECT_ES_UPLOAD_STATEMENT) {
      setUploadedFiles(
        currentDestinationDetails.destinationStatementFileNames.map((fileName, index) => ({
          file: new File([], fileName),
          id: `${fileName}-${index}`,
          fileName,
          status: "completed" as UploadedFile["status"],
          progress: 100,
          fileSize: "",
        })),
      );
    } else if (currentScreen === STEP_DESTINATION_FLASH_LOADING && transferIntentDetails) {
      if (transferIntentDetails.primaryDestination) {
        setCurrentDestinationDetails(
          TransferIntent.convertIntentToDestination(transferIntentDetails.primaryDestination),
        );
        setIsEmployerSponsored(
          !(transferIntentDetails.primaryDestination as PrimaryDestinationChoice).type.toLowerCase().includes("ira"),
        );
      } else if (transferIntentDetails?.destinationAccounts && transferIntentDetails?.destinationAccounts[0]) {
        setCurrentDestinationDetails(
          TransferIntent.convertIntentToDestination(transferIntentDetails.destinationAccounts[0]),
        );
        setIsEmployerSponsored(!transferIntentDetails.destinationAccounts[0].type.toLowerCase().includes("ira"));
      }
    }
  }, [currentScreen]);

  const uploadFileToAPI = async (uploadingFile: UploadedFile): Promise<FileUploadStatus> => {
    const progressInterval = setInterval(() => {
      setUploadedFiles((prev) =>
        prev.map((file) => {
          if (uploadingFile && file.status === "uploading" && file.progress < 80) {
            return { ...file, progress: Math.min(file.progress + 10, 80) };
          }
          return file;
        }),
      );
    }, 200);
    if (appEnv === BRIDGE_DEMO) {
      await manualWait(3000);

      setUploadedFiles((prev) =>
        prev.map((uploadedFie) =>
          uploadedFie.id === uploadingFile.id
            ? { ...uploadingFile, status: "completed" as const, progress: 100 }
            : uploadingFile,
        ),
      );
      clearInterval(progressInterval);
      const updatedDestinationDetails = new DestinationAccount({
        ...currentDestinationDetails,
        statementSkipped: "FALSE",
        destinationStatementFileNames: [
          ...currentDestinationDetails.destinationStatementFileNames,
          uploadingFile.fileName,
        ],
      });
      setCurrentDestinationDetails(updatedDestinationDetails);
      setIsUploading(false);
      return UPLOAD_SUCCESS;
    } else {
      const status = await uploadDestiationFile(uploadingFile);
      if (status === UPLOAD_SUCCESS) {
        setUploadedFiles((prev) =>
          prev.map((uploadedFie) =>
            uploadedFie.id === uploadingFile.id
              ? { ...uploadingFile, status: "completed" as const, progress: 100 }
              : uploadingFile,
          ),
        );
        const updatedDestinationDetails = new DestinationAccount({
          ...currentDestinationDetails,
          statementSkipped: "FALSE",
          destinationStatementFileNames: [
            ...currentDestinationDetails.destinationStatementFileNames,
            uploadingFile.fileName,
          ],
        });
        setCurrentDestinationDetails(updatedDestinationDetails);
      } else {
        // TODO: do something when
        setUploadedFiles((prev) => prev.filter((uploadedFie) => uploadedFie.id === uploadingFile.id));
      }
      setIsUploading(false);
      clearInterval(progressInterval);
      return status;
    }
  };

  const handleUploadFile = async (files: UploadedFile[]): Promise<FileUploadStatus> => {
    setIsUploading(true);
    const uploadingFile = {
      file: files[0].file,
      fileName: files[0].fileName,
      id: `${files[0].fileName}-${Date.now()}`,
      status: "uploading" as const,
      progress: 0,
      fileSize: files[0].fileSize,
    };

    setUploadedFiles([...uploadedFiles, uploadingFile]);
    return await uploadFileToAPI(uploadingFile);
  };

  const handleRemoveFile = async (fileName: string) => {
    const updatedDestinationDetails = new DestinationAccount({
      ...currentDestinationDetails,
      destinationStatementFileNames: currentDestinationDetails.destinationStatementFileNames.filter(
        (e) => e === fileName,
      ),
      statementSkipped: "TRUE",
    });
    if (appEnv !== BRIDGE_DEMO) {
      // REMOVE IN API
      await Promise.all([
        deleteDestinationAccountStatement(fileName),
        // createDestainationInAPI(updatedDestinationDetails),
      ]);
    }
    setCurrentDestinationDetails(updatedDestinationDetails);
    setUploadedFiles(uploadedFiles.filter((e) => e.fileName === fileName));
    setIsUploading(false);
  };

  const onClickSkip = () => {
    setCurrentDestinationDetails(new DestinationAccount());
    goToScreen(STEP_DESTINATION_OPTIONS);
  };

  const updateDestinationDetails = (updatedData: Partial<DestinationAccount>) => {
    setCurrentDestinationDetails({ ...currentDestinationDetails, ...updatedData });
  };

  const onClickSkipStatement = () => {
    goToScreen(STEP_DESTINATION_CONNECT_ES_DETAILS);
  };

  const onClickExploreMyOptions = async () => {
    Analytics.track({ name: "Clicked Explore my options" });
    if (appEnv === BRIDGE_DEMO) {
      goToScreen(STEP_DESTINATION_HELP);
    } else {
      const transferOptionsRes = await getUserTransferOptions();
      if (!transferOptionsRes) {
        goToScreen(STEP_DESTINATION_HELP);
      } else {
        goToScreen(STEP_DESTINATION_HELP_RESULT);
      }
    }
  };

  const onClickDifferentDestination = async () => {
    // step 1: delete current destination
    await deleteCurrentDestination(currentDestinationDetails.destinationAccountId);
    // take back to explore options
    if (transferOptions) {
      goToScreen(STEP_DESTINATION_HELP_RESULT);
    } else {
      goToScreen(STEP_DESTINATION_OPTIONS);
    }
  };

  const [hasIRAAccountNumber, setHasIRAAccountNumber] = useState<boolean>(false);
  const [providerIRAName, setProviderIRAName] = useState<string>("");

  useEffect(() => {
    if (!transferIntentDetails) return;

    if (transferIntentDetails?.destinationAccounts && transferIntentDetails?.destinationAccounts[0]?.accountNumber) {
      setHasIRAAccountNumber(true);
    } else if (transferIntentDetails?.destinationAccounts && transferIntentDetails?.destinationAccounts.length > 0) {
      setProviderIRAName(transferIntentDetails.destinationAccounts[0].providerName);
    }
  }, [transferIntentDetails?.destinationAccounts, transferIntentDetails?.version]);

  const onConfirmUserChoice = () => {
    if (userChoice === "EXPLORE_MY_OPTIONS") {
      onClickExploreMyOptions();
    } else {
      goToScreen(STEP_DESTINATION_OPTIONS_TYPE);
    }
  };

  const confirmDestinationChoice = async (destinationChoice: TransferIntentDestination) => {
    setDestinationChoiceError("");
    setSelectedClientDestination(TransferIntent.convertIntentToDestination(destinationChoice));
  };

  const confirmClientDestinationSelection = async () => {
    if (!selectedClientDestination) {
      setDestinationChoiceError("Please select an option to continue");
      return;
    }
    await createDestainationInAPI(selectedClientDestination);
    setCurrentDestinationDetails(selectedClientDestination);
    goToScreen(STEP_DESTINATION_ACCOUNT);
  };
  return {
    isLoading,
    onClickContinue,
    currentDestinationDetails,
    currentDestinationStep: currentScreen,
    setIsLoading,
    transferIntentDetails,
    hasIRAAccountNumber,
    providerIRAName,
    onClickSkip,
    goToDestinationScreen: (screen: DestinationStep) => goToScreen(screen),
    updateDestinationDetails,
    getEmployerOptions,
    onClickSkipStatement,
    providerList,
    handleUploadFile,
    uploadedFiles,
    goToScreen,
    formValidation,
    confirmedTradIRANumber,
    setConfirmedTradIRANumber,
    confirmedRothIRANumber,
    setConfirmedRothIRANumber,
    isUploading,
    onClickExploreMyOptions,
    onClickDifferentDestination,
    lockProvider,
    handleRemoveFile,
    setIsDestinationAnimationLoaded,
    isIRAFoundAnimationLoaded,
    setIsIRAFoundAnimationLoaded,
    setSourceOfDestination,
    sourceOfDestination,
    clientDestinationOptions: transferIntentDetails?.destinationAccounts,
    isEmployerSponsored,
    setIsEmployerSponsored,
    redirectToOptions,
    userChoice,
    setUserChoice,
    onConfirmUserChoice,
    confirmDestinationChoice,
    confirmClientDestinationSelection,
    selectedClientDestination,
    destinationChoiceError,
  };
};

export default useDestinationAccount;
